import {memo, useMemo} from 'react';
import PropTypes from 'prop-types';
import {FormattedMessage, useIntl} from 'react-intl';

import {
  Box,
  Skeleton,
  SkeletonText,
  Text,
  useBreakpointValue,
  useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui';
import BaseLink from '@amblique/base/app/components/link';
import {Markup} from '@amblique/base/app/components/shared/markup';
import {resolveContentfulLinkUrl} from '@amblique/king-living/app/contentful/helpers/resolve-contentful-link-url';
import {GET_TILE_HYDRATE_PARAMS} from '@amblique/king-living/app/utils/api-utils';
import {getCoverSwatches} from '@amblique/king-living/app/utils/product-cover-swatches';
import {buildSwatchSrc} from '@amblique/king-living/app/utils/responsive-image-src';
import {useProduct} from '@amblique/king-living/app/hooks/use-product';
import {Swiper} from '@amblique/king-living/app/components/shared/swiper';
import {Component} from '@amblique/king-living/app/components/contentful/component';
/** Clicks on in-tile Swiper chrome must not trigger category navigation. */
const INNER_TILE_SWIPER_CHROME =
  '.swiper-button-next, .swiper-button-prev, .swiper-pagination, .swiper-scrollbar, .swiper-pagination-progressbar, .swiper-pagination-bullet';

const isInnerTileSwiperChrome = (target) => Boolean(target?.closest?.(INNER_TILE_SWIPER_CHROME));

const APPEARANCE_TEXT_ABOVE_SWATCH = 'Text above swatch';
const APPEARANCE_TEXT_BELOW_SWATCH = 'Text below swatch';
const APPEARANCE_TILE = 'Teaser Tile';

const DEFAULT_APPEARANCE = APPEARANCE_TEXT_BELOW_SWATCH;

const APPEARANCE_VARIANT_MAP = {
  [APPEARANCE_TEXT_ABOVE_SWATCH]: 'textAboveSwatch',
  [APPEARANCE_TEXT_BELOW_SWATCH]: 'textBelowSwatch',
  [APPEARANCE_TILE]: 'tile',
};

const SWATCH_CAP_DESKTOP = 6;
const SWATCH_CAP_MOBILE = 4;

const SwatchRow = memo(({swatchCapMobile, swatches, styles}) => {
  const intl = useIntl();
  const mobileCap = swatchCapMobile ?? SWATCH_CAP_MOBILE;
  const cap = useBreakpointValue({base: mobileCap, lg: SWATCH_CAP_DESKTOP});
  const totalSwatches = swatches.length;
  const activeCap = cap ?? mobileCap;
  const renderedSwatches = useMemo(() => swatches.slice(0, activeCap), [swatches, activeCap]);
  const hasOverflow = totalSwatches > activeCap;

  if (!renderedSwatches.length) return null;

  return (
    <Box
      className="contentful-card__swatch-row"
      role="img"
      aria-label={intl.formatMessage(
        {
          defaultMessage: 'Available in {count, plural, one {# cover} other {# covers}}',
          id: 'contentful_card.swatches.available',
        },
        {count: totalSwatches}
      )}
      {...styles.swatchRow}
    >
      {renderedSwatches.map((s, i) => {
        const swatchUrl = buildSwatchSrc(s.swatchUrl);

        return (
          <Box
            key={s.value ?? s.name ?? i}
            as="span"
            backgroundImage={swatchUrl ? `url(${swatchUrl})` : undefined}
            className="contentful-card__swatch-item"
            title={s.name}
            {...styles.swatchItem}
          />
        );
      })}

      {hasOverflow && (
        <Text as="span" className="contentful-card__swatch-more" {...styles.swatchMore}>
          <Box
            as="span"
            className="contentful-card__swatch-more-label contentful-card__swatch-more-label--mobile"
            display={{base: 'inline', lg: 'none'}}
          >
            <FormattedMessage defaultMessage="+ more" id="contentful_card.swatches.more" />
          </Box>
          <Box
            as="span"
            className="contentful-card__swatch-more-label contentful-card__swatch-more-label--desktop"
            display={{base: 'none', lg: 'inline'}}
          >
            <FormattedMessage
              defaultMessage="+ more colours & sizes"
              id="contentful_card.swatches.more_full"
            />
          </Box>
        </Text>
      )}
    </Box>
  );
});

SwatchRow.displayName = 'SwatchRow';
SwatchRow.propTypes = {
  swatchCapMobile: PropTypes.number,
  swatches: PropTypes.array.isRequired,
  styles: PropTypes.object.isRequired,
};

/**
 * @override
 * Card contentful model — three appearance variants:
 *   - "Text above swatch": image → text → swatches (mobile swipe uses range_tile progress bar)
 *   - "Text below swatch": hoverWrapper(primaryBlock ↔ lifestyleBlock) → text
 *   - "Teaser Tile":       image → description + CTA
 */
export const Card = ({component, isComplete = true, isFetching = false, swatchCapMobile, ...props}) => {
  const {
    appearance,
    badge,
    badgeBackgroundColor,
    badgeTextColor,
    category,
    customImage,
    description,
    lifestyleImage,
    primaryImage,
    productId,
    showColorOptions,
  } = component;

  const categoryUrl = useMemo(() => resolveContentfulLinkUrl(category), [category]);
  const categoryOpenInNewTab = category?.openInNewTab;
  const categoryLinkText = category?.linkText ?? category?.text;
  const isExternalCategoryUrl =
    Boolean(categoryUrl) &&
    (categoryUrl.startsWith('http://') ||
      categoryUrl.startsWith('https://') ||
      categoryUrl.startsWith('//'));

  const resolvedAppearance = appearance ?? DEFAULT_APPEARANCE;
  const variant = APPEARANCE_VARIANT_MAP[resolvedAppearance] ?? 'textBelowSwatch';
  const isTile = resolvedAppearance === APPEARANCE_TILE;
  const isTextBelowSwatch = resolvedAppearance === APPEARANCE_TEXT_BELOW_SWATCH;
  const isTextAboveSwatch = resolvedAppearance === APPEARANCE_TEXT_ABOVE_SWATCH;

  const styles = useMultiStyleConfig('contentful/card', {component, isComplete, isFetching, variant});
  const isMobile = useBreakpointValue({base: true, lg: false});

  const shouldFetchProduct = Boolean(showColorOptions && productId);
  const {data: product} = useProduct(
    {parameters: {id: productId, ...GET_TILE_HYDRATE_PARAMS}},
    {enabled: shouldFetchProduct}
  );

  const mainImageUrl = isTile ? (customImage?.url ?? primaryImage?.url) : primaryImage?.url ?? null;
  const hoverImageUrl = lifestyleImage?.url ?? null;
  const hasLifestyleImage = Boolean(hoverImageUrl);

  const swatches = useMemo(
    () => (shouldFetchProduct && product ? getCoverSwatches(product) : []),
    [product, shouldFetchProduct]
  );
  const hasSwatches = swatches.length > 0;

  const renderCategoryLinkedArea = (content, linkProps = {}) => {
    if (!categoryUrl) {
      return content;
    }

    return (
      <BaseLink
        aria-label={categoryLinkText || undefined}
        className="contentful-card__image-link"
        cursor="pointer"
        display="block"
        href={isExternalCategoryUrl ? categoryUrl : undefined}
        rel={categoryOpenInNewTab ? 'noopener noreferrer' : undefined}
        target={categoryOpenInNewTab ? '_blank' : undefined}
        to={!isExternalCategoryUrl ? categoryUrl : undefined}
        onClick={(event) => {
          if (isInnerTileSwiperChrome(event.target)) {
            event.preventDefault();
            event.stopPropagation();
          }
        }}
        {...linkProps}
      >
        {content}
      </BaseLink>
    );
  };

  // Must be declared before early return so it's available to both render branches below.
  const swatchSectionEl = hasSwatches && (
    <Box className="contentful-card__swatch-section" {...styles.swatchSection}>
      <SwatchRow styles={styles} swatchCapMobile={swatchCapMobile} swatches={swatches} />
    </Box>
  );

  if (!isComplete && isFetching) {
    return (
      <Box className="contentful-card__root" {...styles.root} {...props}>
        <Skeleton {...styles.skeletonImage} />
        <Box {...styles.content}>
          <SkeletonText {...styles.skeletonText} />
        </Box>
      </Box>
    );
  }

  const badgeEl = badge && (
    <Box
      className="contentful-card__badge"
      {...styles.badge}
      sx={{
        ...(badgeBackgroundColor ? {bg: badgeBackgroundColor} : {}),
        ...(badgeTextColor ? {color: badgeTextColor} : {}),
      }}
    >
      {badge}
    </Box>
  );


  const imageWrapperEl = (
    <Box
      className="contentful-card__image-wrapper"
      data-has-lifestyle={!isTextBelowSwatch && hasLifestyleImage ? true : undefined}
      {...styles.imageWrapper}
    >
      {isMobile && hasLifestyleImage ? (
        <Swiper presets="range_tile">
          <Box
            alt={description ?? ''}
            as="img"
            className="contentful-card__image contentful-card__image--primary"
            loading="eager"
            src={mainImageUrl}
            {...styles.swiperImage}
          />
          <Box
            alt=""
            aria-hidden="true"
            as="img"
            className="contentful-card__image contentful-card__image--lifestyle"
            loading="eager"
            src={hoverImageUrl}
            {...styles.swiperImage}
          />
        </Swiper>
      ) : (
        <>
          {mainImageUrl && (
            <Box
              alt={description ?? ''}
              as="img"
              className="contentful-card__image contentful-card__image--primary"
              loading="lazy"
              src={mainImageUrl}
              {...styles.image}
            />
          )}
          {!isTextBelowSwatch && hasLifestyleImage && (
            <Box
              alt=""
              aria-hidden="true"
              as="img"
              className="contentful-card__image contentful-card__image--lifestyle"
              loading="lazy"
              src={hoverImageUrl}
            />
          )}
        </>
      )}

      {isTextBelowSwatch && swatchSectionEl}
    </Box>
  );

  const contentSection = (
    <Box className={`contentful-card__content ${isTile ? 'contentful-card__content--tile' : ''}`} {...styles.content}>
      {!isTile && category && (
        <Component
          className="contentful-card__category-link"
          component={category}
          {...styles.categoryLink}
        />
      )}
      {description && (
        <Markup className={`contentful-card__description ${isTile ? 'contentful-card__description--tile' : ''}`} source={description} {...styles.description} />
      )}
      {isTile && category && (
        <Component
          className="contentful-card__cta-button"
          component={category}
        />
      )}
    </Box>
  );

  if (isTextBelowSwatch) {
    const primaryBlock = (
      <Box className="contentful-card__primary-block" {...styles.primaryBlock}>
        {imageWrapperEl}
      </Box>
    );

    // Lifestyle block is desktop-only — mobile uses the Swiper inside imageWrapperEl.
    const lifestyleBlock = !isMobile && hasLifestyleImage && (
      <Box className="contentful-card__lifestyle-block" {...styles.lifestyleBlock}>
        <Box
          alt=""
          aria-hidden="true"
          as="img"
          className="contentful-card__image contentful-card__image--lifestyle"
          loading="eager"
          src={hoverImageUrl}
          {...styles.lifestyleImage}
        />
      </Box>
    );

    const imageAreaEl =
      !isMobile && hasLifestyleImage ?
        renderCategoryLinkedArea(
          <Box className="contentful-card__hover-wrapper" {...styles.hoverWrapper}>
            {primaryBlock}
            {lifestyleBlock}
          </Box>
        ) :
        renderCategoryLinkedArea(primaryBlock);

    return (
      <Box
        className={`contentful-card__root contentful-card__root--${variant}`}
        {...styles.root}
        {...props}
      >
        {badgeEl}
        {imageAreaEl}
        {contentSection}
      </Box>
    );
  }

  return (
    <Box
      className={`contentful-card__root contentful-card__root--${variant}`}
      {...styles.root}
      {...props}
    >
      {badgeEl}
      {renderCategoryLinkedArea(imageWrapperEl)}
      {isTextAboveSwatch ? (
        <Box className="contentful-card__info-stack" {...styles.infoStack}>
          {contentSection}
          {swatchSectionEl}
        </Box>
      ) : (
        contentSection
      )}
    </Box>
  );
};

Card.displayName = 'Card';
Card.propTypes = {
  component: PropTypes.object.isRequired,
  isComplete: PropTypes.bool,
  isFetching: PropTypes.bool,
  swatchCapMobile: PropTypes.number,
};
